import { useCallback, useState } from 'react'
import * as Dropzone from 'react-dropzone'
import {
  FileListOptions,
  FilePickerOptions,
  FileRejection,
  FileRejectionCode,
  OnRemove
} from '../types'
import { formatBytes } from '../utils'

export const useFilePicker = (
  { fileList, ...filePickerOptions }: FilePickerOptions,
  fileListOptions: FileListOptions
) => {
  const [files, setFiles] = useState<Dropzone.FileWithPath[]>([])
  const [fileRejections, setFileRejections] = useState<FileRejection[]>([])

  const onDropAccepted = useCallback(
    (files: Dropzone.FileWithPath[]) => {
      filePickerOptions.onDropAccepted(files)

      if (!filePickerOptions.multiple) {
        setFileRejections([])
      }

      if (fileList) {
        return
      }

      filePickerOptions.multiple ? setFiles((prev) => [...prev, ...files]) : setFiles(files)
    },
    [fileList, filePickerOptions]
  )

  const onDropRejected = useCallback((fileRejections: Dropzone.FileRejection[]) => {
    const errors: FileRejection[] = []

    for (const rejection of fileRejections) {
      // Only show the first error in the UI for now
      if (rejection.errors[0].code === FileRejectionCode.FILE_TOO_LARGE) {
        errors.push({
          code: FileRejectionCode.FILE_TOO_LARGE,
          message: `File is is larger than ${formatBytes(filePickerOptions.maxSize!)}`,
          file: rejection.file
        })
      } else if (rejection.errors[0].code === FileRejectionCode.FILE_NAME_INVALID) {
        errors.push({
          code: FileRejectionCode.FILE_NAME_INVALID,
          message: rejection.errors[0].message || 'File name is invalid',
          file: rejection.file
        })
      }
    }

    setFileRejections(errors)

    if (filePickerOptions.onDropRejected) {
      filePickerOptions.onDropRejected(errors)
    }

    if (!filePickerOptions.multiple) {
      setFiles([])
    }
  }, [])

  const { getInputProps, getRootProps } = Dropzone.useDropzone({
    ...filePickerOptions,
    onDropAccepted,
    onDropRejected,
    disabled: filePickerOptions.isDisabled,
    useFsAccessApi: false
  })

  const handleRemove: OnRemove = (file, index) => {
    if (fileListOptions.onRemove) {
      fileListOptions.onRemove(file, index)
    } else {
      setFiles((prev) => prev.filter((_x, i) => i !== index))
    }
  }

  const handleRejectionRemove = (file: Dropzone.FileWithPath, index: number) => {
    setFileRejections((prev) => prev.filter((_x, i) => i !== index))

    if (fileListOptions.onRemoveError) {
      fileListOptions.onRemoveError(file, index)
    }
  }

  const resolveInputProps = () => {
    const inputProps = getInputProps()

    if (filePickerOptions.folderDrop) {
      return {
        ...inputProps,
        webkitdirectory: '',
        directory: '',
        mozdirectory: ''
      }
    }

    return inputProps
  }

  return {
    inputProps: resolveInputProps(),
    getRootProps,
    files: fileList ?? files,
    fileRejections,
    handleRemove,
    handleRejectionRemove
  }
}
